import * as THREE from 'three';

type Casing = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  rotVel: THREE.Vector3;
  life: number;
  maxLife: number;
  bounces: number;
  active: boolean;
};

export type FpsBulletCasingSpawnOptions = {
  count?: number;
  velocityMul?: number;
  scale?: number;
  lifeMul?: number;
};

const POOL_SIZE = 48;

export class FpsBulletCasingVfx {
  private readonly casings: Casing[] = [];
  private readonly pool: THREE.Mesh[] = [];
  private readonly geo = new THREE.CylinderGeometry(0.015, 0.015, 0.065, 4);
  private readonly mat = new THREE.MeshToonMaterial({
    color: 0xd4af37,
    emissive: 0x5a4110,
    emissiveIntensity: 0.2,
  });

  constructor(private readonly scene: THREE.Scene) {
    for (let i = 0; i < POOL_SIZE; i++) {
      const mesh = new THREE.Mesh(this.geo, this.mat);
      mesh.visible = false;
      this.scene.add(mesh);
      this.pool.push(mesh);
    }
  }

  spawn(pos: THREE.Vector3, lookDir: THREE.Vector3, options: FpsBulletCasingSpawnOptions = {}): void {
    const count = Math.max(1, Math.min(3, Math.round(options.count ?? 1)));
    const velocityMul = Math.max(0.25, Math.min(2.2, options.velocityMul ?? 1));
    const scale = Math.max(0.55, Math.min(1.8, options.scale ?? 1));
    const lifeMul = Math.max(0.45, Math.min(1.5, options.lifeMul ?? 1));

    for (let i = 0; i < count; i++) {
      const mesh = this.acquireMesh();
      if (!mesh) break;

      mesh.scale.setScalar(scale * (0.92 + Math.random() * 0.16));
      mesh.position.copy(pos);
      mesh.visible = true;

      const right = new THREE.Vector3(1, 0, 0)
        .applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, -1), lookDir));
      mesh.position.addScaledVector(right, 0.18 + i * 0.035);
      mesh.position.y -= 0.12;
      mesh.position.addScaledVector(lookDir, (Math.random() - 0.5) * 0.05);

      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);

      const ejectVel = right.clone().multiplyScalar((1.8 + Math.random() * 1.2) * velocityMul);
      ejectVel.y += (1.1 + Math.random() * 0.9) * velocityMul;
      ejectVel.addScaledVector(lookDir, (-0.4 - Math.random() * 0.4) * velocityMul);

      const rotVel = new THREE.Vector3(
        8 + Math.random() * 14,
        8 + Math.random() * 14,
        8 + Math.random() * 14,
      );

      this.casings.push({
        mesh,
        vel: ejectVel,
        rotVel,
        life: 0,
        maxLife: (1.6 + Math.random() * 0.6) * lifeMul,
        bounces: 0,
        active: true,
      });
    }
  }

  update(dt: number, groundY = 0): void {
    for (let i = this.casings.length - 1; i >= 0; i--) {
      const c = this.casings[i]!;
      if (!c.active) continue;
      c.life += dt;

      c.vel.y -= dt * 9.81;
      c.mesh.position.addScaledVector(c.vel, dt);
      c.mesh.rotation.x += c.rotVel.x * dt;
      c.mesh.rotation.y += c.rotVel.y * dt;
      c.mesh.rotation.z += c.rotVel.z * dt;

      const localGroundY = groundY + 0.05;
      if (c.mesh.position.y <= localGroundY) {
        c.mesh.position.y = localGroundY;

        if (c.bounces < 3) {
          c.vel.y = -c.vel.y * 0.38;
          c.vel.x *= 0.5;
          c.vel.z *= 0.5;
          c.rotVel.multiplyScalar(-0.4);
          c.bounces += 1;
        } else {
          c.vel.set(0, 0, 0);
          c.rotVel.set(0, 0, 0);
        }
      }

      const ratio = c.life / c.maxLife;
      if (ratio > 0.8) {
        const fadeScale = 1 - (ratio - 0.8) / 0.2;
        c.mesh.scale.setScalar(Math.max(0, fadeScale * c.mesh.scale.x));
      }

      if (c.life >= c.maxLife) {
        this.releaseCasing(i);
      }
    }
  }

  private acquireMesh(): THREE.Mesh | null {
    for (const mesh of this.pool) {
      if (!mesh.visible) return mesh;
    }
    return null;
  }

  private releaseCasing(index: number): void {
    const c = this.casings[index]!;
    c.mesh.visible = false;
    c.mesh.position.set(0, -1000, 0);
    c.active = false;
    this.casings.splice(index, 1);
  }

  dispose(): void {
    for (const mesh of this.pool) {
      this.scene.remove(mesh);
    }
    this.pool.length = 0;
    this.casings.length = 0;
    this.geo.dispose();
    this.mat.dispose();
  }
}
